import { useRef, useState } from "react";
import type { MealRepository } from "../../application/meal-repository";
import { MealWriteQueue } from "../../application/meal-write-queue";
import { isMealNutritionSaveable, type ParsedVoiceMealDraft } from "../../application/remote-meal-parser";

/** Owns save status and ordering for one editor, including voice changes and transfers. */
export function useMealPersistence(repository: MealRepository, selectedDate: string) {
  const [status, setStatus] = useState<"idle" | "saving" | "error">("idle");
  const saved = useRef(false);
  const queue = useRef(new MealWriteQueue()).current;

  const save = async (draft: ParsedVoiceMealDraft, replace: boolean): Promise<boolean> => {
    setStatus("saving");
    try {
      await queue.enqueue(async () => {
        if (replace || saved.current) await repository.replaceMealType(selectedDate, draft);
        else await repository.saveDraft(selectedDate, draft);
        saved.current = true;
      });
      setStatus("idle");
      return true;
    } catch {
      setStatus("error");
      return false;
    }
  };
  const persist = async (draft: ParsedVoiceMealDraft, replace: boolean) => {
    if (isMealNutritionSaveable(draft)) await save(draft, replace);
  };
  const markSaved = (value: boolean) => { saved.current = value; };
  const resetStatus = () => setStatus("idle");
  return { status, queue, save, persist, markSaved, resetStatus, isSaved: () => saved.current };
}
